import React, { useState, useEffect, useRef, useContext } from 'react';
import Context from '../../../context/Context';
import { Input, Table, Tag, message, Pagination } from 'antd';
import { debounce } from 'lodash';
import { IoSearchOutline } from 'react-icons/io5';

import { api } from '../../../api/api';
import SuccessIcon from '../../../assets/successicon.svg';

import styles from './completedList.module.scss';
import moment from 'moment';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
const getFacilityNamesFromCodes = (codesStr, facilities = []) => {
	if (!codesStr) return '';

	const codes = String(codesStr)
		.split(',')
		.map((c) => c.trim())
		.filter(Boolean);

	const map = new Map(
		(facilities || []).map((f) => [String(f.facility).trim(), f.facilityName])
	);

	return codes.map((code) => map.get(code) || code).join(', ');
};

const getClinicalFacilityNamesFromCodes = (codesStr, facilities = []) => {
	if (!codesStr) return [];

	const codes = String(codesStr)
		.split(',')
		.map((c) => c.trim())
		.filter(Boolean);

	const safeFacilities = Array.isArray(facilities) ? facilities : [];

	const map = new Map(
		safeFacilities.map((f) => [
			String(f.clinicalFacilityCode).trim(),
			f.facilityName,
		])
	);

	return codes.map((code) => map.get(code) || code);
};

const CompletedList = () => {
	const userDetails = JSON.parse(localStorage.getItem('user-onboard-data'));
	const isAdmin = userDetails?.isAdmin === 'Y';
	const { SSPFacilities } = useContext(Context);
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [tableParams, setTableParams] = useState({
		pagination: {
			current: 1,
			pageSize: 10,
			showSizeChanger: true,
			showQuickJumper: true,
			total: 0,
			// showTotal: (total) => `Total ${total} items`,
		},
	});
	const [visibleNric, setVisibleNric] = useState({});
	const toggleNric = (id) => {
		setVisibleNric((prev) => {
			const isNowVisible = !prev[id];

			if (isNowVisible) {
				setTimeout(() => {
					setVisibleNric((p) => ({
						...p,
						[id]: false,
					}));
				}, 10000);
			}

			return {
				...prev,
				[id]: isNowVisible,
			};
		});
	};
	// Fetch the data from API
	const fetchUserPendingList = async () => {
		setLoading(true);
		try {
			const { current, pageSize } = tableParams.pagination;
			const response = await api.get({
				path:
					userDetails?.isAdmin === 'Y'
						? `/getAdminAllCompletedUsers`
						: `/getUserAllCompletedList?nhName=${userDetails?.nhName}`,
				params: { page: current, size: pageSize, searchText },
				service: 'useronboarding',
			});

			if (response.status === 200) {
				const pendinglist = response.data?.data;
				const totaldata = response.data?.pagination?.totalData;

				setData(pendinglist);

				setTableParams((prev) => ({
					...prev,
					pagination: {
						...prev.pagination,
						total: totaldata || response.data?.data?.length,
					},
				}));
			}
		} catch (error) {
			message.error('Something went wrong!');
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange = (pagination) => {
		setTableParams((prev) => ({
			...prev,
			pagination,
		}));
	};

	const handleSearch = (e) => {
		console.log(e.target.value.length);
		if (e.target.value.length >= 3) {
			setSearchText(e.target.value);
			setTableParams((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					current: 1,
				},
			}));
		}
	};

	useEffect(() => {
		fetchUserPendingList(); //eslint-disable-next-line
	}, [
		tableParams.pagination.current,
		tableParams.pagination.pageSize,
		searchText,
	]);

	const debouncedSearch = useRef(debounce(handleSearch, 300)).current;

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		}; //eslint-disable-next-line
	}, []);

	const getStatusTag = (status) => {
		switch (status) {
			case "C":
				return <span className={styles.badgeCompleted}>Completed</span>;
			case "R":
				return <span className={styles.badgeRejected}>Rejected</span>;
			case "P":
				return <span className={styles.badgePending}>Pending</span>;
			default:
				return <span className={styles.badgeUnknown}>Unknown</span>;
		}
	};

	// Define the columns for the AntD table
	const columns = [
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 130,
			render: (value) => getStatusTag(value),
		},
		{
			title: 'Submitted By',
			dataIndex: 'createdBy',
			key: 'createdBy',
			width: 160,
		},
		{
			title: 'Permitted Facilities',
			dataIndex: 'nhAdminFacilities',
			key: 'nhAdminFacilities',
			render: (value) => {
				const text = getFacilityNamesFromCodes(value, SSPFacilities?.data || []);
				if (!text) return '-';

				const items = text.split(',').map(s => s.trim()).filter(Boolean);

				return (
					<div>
						{items.map((item, idx) => (
							<div key={`${item}-${idx}`}>{item}</div>
						))}
					</div>
				);
			},
		},
		{
			title: 'Submitted Date',
			dataIndex: 'createdDate',
			key: 'createdDate',
			width: 170,
			render: (value) => value && moment(value).format('DD-MMM-YYYY'),
		},
		{
			title: 'Effective Start Date',
			dataIndex: 'effectiveStartDate',
			key: 'effectiveStartDate',
			onHeaderCell: () => ({ style: { minWidth: 150 } }),
			onCell: () => ({ style: { minWidth: 150 } }),
			render: (value) => value && moment(value).format('DD-MMM-YYYY'),
		},
		{
			title: 'User Name',
			dataIndex: 'userName',
			key: 'userName',
			width: 160,
		},
		{
			title: 'First Name',
			dataIndex: 'firstName',
			key: 'firstName',
			width: 160,
		},
		{
			title: 'Last Name',
			dataIndex: 'lastName',
			key: 'lastName',
			width: 160,
		},
		{
			title: 'SSO Facility Code',
			dataIndex: 'facilitycode',
			key: 'facilityCode',
			width: 190,
		},
		{
			title: 'NH Admin Required',
			dataIndex: 'nhAdminRequired',
			key: 'nhAdminRequired',
			width: 210,
			render: (value) =>
				value === true || value === '1' || value === 1 ? 'Yes' : 'No',
		},
		{
			title: 'NRIC / FIN',
			dataIndex: 'nric',
			minWidth: 200,
			render: (value, record) => {
				if (!value) return '-';

				const isVisible = visibleNric[record.id];

				const masked = value.replace(/.(?=.{4})/g, '*');

				return (
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span>{isVisible ? value : masked}</span>

						{isVisible ? (
							<EyeInvisibleOutlined
								className={styles.nricEyeIcon}
								onClick={() => toggleNric(record.id)}
							/>
						) : (
							<EyeOutlined
								className={styles.nricEyeIcon}
								onClick={() => toggleNric(record.id)}
							/>
						)}
					</div>
				);
			},
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
			width: 260,
		},
		{
			title: 'Telephone Number',
			dataIndex: 'telephoneNumber',
			key: 'telephoneNumber',
			width: 210,
		},
		{
			title: 'App Access',
			dataIndex: 'serviceComp',
			key: 'serviceComp',
			width: 160,
		},
		{
			title: 'Access Required?',
			dataIndex: 'access',
			key: 'appAccess',
			width: 220,
			render: (_, record) => (
				<>
					{record.hrAccessRequired && <Tag color="magenta">HR</Tag>}
					{record.financeAccessRequired && <Tag color="magenta">Finance</Tag>}
					{record.clinicalAccessRequired && <Tag color="magenta">Clinical</Tag>}
				</>
			),
		},
		{
			title: 'Requested Clinical Facilities',
			dataIndex: 'clinicalFacilityCode',
			key: 'clinicalFacilityCode',
			width: 230,
			render: (value) => {

				const facilityNames = getClinicalFacilityNamesFromCodes(
					value,
					SSPFacilities?.data || []
				);

				if (!facilityNames.length) return '-';

				return (
					<div>
						{facilityNames.map((name, idx) => (
							<div key={idx}>{name}</div>
						))}
					</div>
				);
			},
		},
		{
			title: 'Clinical Designation',
			dataIndex: 'classificationDesc',
			key: 'classificationDesc',
			width: 230,
			render: (value) => value && <Tag color="magenta">{value}</Tag>,
		},
		{
			title: 'MICR Code',
			dataIndex: 'micrCode',
			key: 'micrCode',
			width: 160,
		},
		{
			title: 'Clinical Roles',
			dataIndex: 'clinicalRolesDesc',
			key: 'clinicalRolesDesc',
			width: 260,
			render: (value) =>
				value &&
				value.split(',').map((el) => (
					<Tag key={el} color="magenta">
						{el}
					</Tag>
				)),
		},
		{
			title: 'Financial Roles',
			dataIndex: 'financeRolesDesc',
			key: 'financeRolesDesc',
			width: 260,
			render: (value) =>
				value &&
				value.split(',').map((el) => (
					<Tag key={el} color="magenta">
						{el}
					</Tag>
				)),
		},
		{
			title: 'Existing NHELP/ILTC/IRMS Username',
			dataIndex: 'userName',
			key: 'existingUserName',
			width: 320,
		},
		{
			title: 'Remarks',
			dataIndex: 'remarks',
			key: 'remarks',
			width: 240,
		},
		{
			title: 'Completion Email to be sent',
			dataIndex: 'completionEmailList',
			key: 'completionEmailList',
			width: 300,
		},
		isAdmin && {
			title: 'Approved By',
			dataIndex: 'modifiedBy',
			key: 'modifiedBy',
			onHeaderCell: () => ({ style: { minWidth: 300 } }),
			onCell: () => ({ style: { minWidth: 300 } }),
		},
		isAdmin && {
			title: 'Approved Date',
			dataIndex: 'modifiedDate',
			key: 'modifiedDate',
			onHeaderCell: () => ({ style: { minWidth: 300 } }),
			onCell: () => ({ style: { minWidth: 300 } }),
			render: (value) => value && moment(value).format('DD-MMM-YYYY'),
		},
	].filter(Boolean);


	return (
		<div className={styles['wrapper']}>
			<div className={styles['top-section']}>
				<div className={styles['top-section-left']}>
					<div className={styles['left']}>
						<p className={styles['title']}>All Completed Users</p>
						<div className={styles['totals']}>
							<img
								className={styles['success-icons']}
								src={SuccessIcon}
								alt="Success Icon"
							/>
							<p className={styles['totals']}>
								Total Insights : {tableParams.pagination.total}
							</p>
						</div>
					</div>
					<div className={styles['right']}>
						<Input
							prefix={<IoSearchOutline />}
							className={styles['search-input']}
							placeholder="Search by firstname or lastname"
							onPressEnter={handleSearch}
							onChange={debouncedSearch}
							allowClear
						/>
					</div>
				</div>
			</div>
			<div className={styles.wrapper}>

				{/* FIXED horizontal scroll */}
				<div className={styles.horizontalFixed}>
					<div className={styles.verticalScroll}>
						<Table
							className={styles.table}
							rowKey={(record) => record.id}
							dataSource={data}
							columns={columns}
							loading={loading}
							pagination={false}
							tableLayout="auto"
							onChange={handleTableChange}
							rowClassName={(_, index) =>
								index % 2 === 0 ? styles.evenRow : styles.oddRow
							}
							onRow={() => ({
								className: styles.rowWrapper,
							})}
							scroll={{
								x: 'max-content',
								y: 450
							}}
						/>
					</div>
				</div>

				{/* FIXED pagination */}
				<div className={styles.paginationWrapper}>
					<Pagination
						current={tableParams.pagination.current}
						pageSize={tableParams.pagination.pageSize}
						total={tableParams.pagination.total}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						onChange={(page, pageSize) =>
							handleTableChange({ current: page, pageSize })
						}
					/>
				</div>

			</div>
		</div>

	);
};

export default CompletedList;
